import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { GoogleLogin } from '@react-oauth/google';
import { loginUser, signupUser, googleAuthUser } from '../../slices/authSlice';
import useFormValidation from '../../hooks/useFormValidation';
import ConsentCheckboxes from '../Consent/ConsentCheckboxes';
import { CONSENT_VERSION } from '../../utils/aiConsent';
import AnimatedTextLoader from '../UIFeedback/AnimatedTextLoader/AnimatedTextLoader';
import { Button } from '../ui/button';
import Logo from '../Logo/Logo';
import BrandName from '../BrandName/BrandName';

const formFields = {
  login: [
    { id: 1, name: 'email', type: 'email', label: 'emailAddress', placeholder: 'emailPlaceholder' },
    { id: 2, name: 'password', type: 'password', label: 'password', placeholder: 'passwordPlaceholder', isPassword: true },
  ],
  signup: [
    { id: 1, name: 'email', type: 'email', label: 'emailAddress', placeholder: 'emailPlaceholder' },
    { id: 2, name: 'password', type: 'password', label: 'password', placeholder: 'passwordPlaceholder', isPassword: true },
  ],
};

const Login = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('success');
  const [consentPart1, setConsentPart1] = useState(false);
  const [consentPart2, setConsentPart2] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(true);
  const [consentError, setConsentError] = useState(false);
  const { loading } = useSelector((state) => state.auth);

  const isSignUp = location.pathname === '/sign-up';

  const { isFormValid: isSignUpValid, errors } = useFormValidation(formData);
  const hasEmailAndPassword =
    formData.email.trim() !== '' && formData.password.trim() !== '';
  const isFormValid = isSignUp
    ? isSignUpValid && hasEmailAndPassword && consentPart1
    : hasEmailAndPassword;

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.(com|org|net|edu|gov|co|io|us|ca|uk)$/i.test(email);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBlur = (e) => {
    setTouchedFields({ ...touchedFields, [e.target.name]: true });
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleAuthSuccess = () => {
    setPopupMessage(isSignUp ? t('signupSuccess') : t('loginSuccess'));
    setPopupType('success');
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
      setConsentPart1(false);
      setConsentPart2(false);
      setMarketingOptIn(true);
      setFormData({
        email: '',
        password: '',
      });
      const from = location.state?.from;
      const openCreatePost = location.state?.openCreatePost;
      const target = typeof from === 'string' && from ? from : '/';
      navigate(target, {
        replace: true,
        state: openCreatePost ? { openCreatePost } : undefined,
      });
    }, 4000);
  };

  const handleAuthError = (error) => {
    setPopupMessage(error || t('errorMessage'));
    setPopupType('error');
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 4000);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (isSignUp && !consentPart1) {
      setConsentError(true);
      return;
    }

    try {
      if (isSignUp) {
        await dispatch(
          signupUser({
            ...formData,
            termsAccepted: consentPart1,
            confirmedAdult: consentPart1,
            aiConsent: consentPart2,
            consentVersion: CONSENT_VERSION,
            marketingOptIn,
          })
        ).unwrap();
      } else {
        await dispatch(loginUser(formData)).unwrap();
      }

      handleAuthSuccess();
    } catch (error) {
      handleAuthError(error);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const credential = credentialResponse?.credential;
    if (!credential) {
      handleAuthError(t('googleSignInFailed', 'Google sign-in failed. Please try again.'));
      return;
    }

    try {
      await dispatch(
        googleAuthUser({
          credential,
          termsAccepted: true,
          confirmedAdult: true,
          consentVersion: CONSENT_VERSION,
        })
      ).unwrap();

      handleAuthSuccess();
    } catch (error) {
      handleAuthError(error);
    }
  };

  const handleGoogleError = () => {
    handleAuthError(t('googleSignInFailed', 'Google sign-in failed. Please try again.'));
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-white px-3 sm:px-4 md:px-6 py-3 sm:py-4 flex items-center justify-between border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="min-w-[36px] min-h-[36px] sm:min-w-[44px] sm:min-h-[44px]"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <h1 className="text-base sm:text-xl font-bold text-foreground">
            {isSignUp ? t('createAccount') : t('loginAccount')}
          </h1>
        </div>
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-primary hover:text-primary/80 font-semibold text-[10px] sm:text-sm px-2 sm:px-3"
        >
          {t('continue_as_guest', 'Continue as guest')}
        </Button>
      </div>

      <div className="flex-1 flex flex-col justify-center px-3 sm:px-4 md:px-6 py-6 sm:py-8 overflow-auto">
        {/* Logo/Branding Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-6 sm:mb-8"
        >
          <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 mx-auto mb-3 sm:mb-4 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
            <Logo className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-1.5 sm:mb-2">
            <BrandName />
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {t('app_tagline', 'Your one-stop solution for global animal care.')}
          </p>
        </motion.div>

        <div className="max-w-md mx-auto w-full mb-4 sm:mb-6">
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text={isSignUp ? 'signup_with' : 'signin_with'}
              shape="pill"
              theme="outline"
              size="large"
              width="384"
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 mt-4 sm:mt-6">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">
              {t('or', 'or')}
            </span>
            <div className="flex-1 h-px bg-border" />
          </div>
        </div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="space-y-3 sm:space-y-4 max-w-md mx-auto w-full"
          onSubmit={handleSubmit}
        >
          {formFields[isSignUp ? 'signup' : 'login'].map((field) => (
            <div key={field.id}>
              <label 
                htmlFor={field.name} 
                className="block text-xs sm:text-sm font-medium text-foreground mb-1.5 sm:mb-2"
              >
                {t(field.label)}
              </label>
              <div className="relative">
                <input
                  type={field.isPassword && showPassword ? 'text' : field.type}
                  id={field.name}
                  name={field.name}
                  value={formData[field.name]}
                  placeholder={t(field.placeholder)}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className="
                    w-full px-3 sm:px-4 py-2.5 sm:py-3 
                    rounded-xl 
                    border-2 border-border 
                    focus:border-primary focus:outline-none 
                    transition-colors 
                    bg-white
                    text-sm sm:text-base text-foreground
                    placeholder:text-muted-foreground
                  "
                  required
                />
                {field.isPassword && (
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="
                      absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 
                      p-1.5 sm:p-2 
                      hover:bg-gray-100 rounded-lg 
                      transition-colors
                    "
                    aria-label={showPassword ? t('hide_password') : t('show_password')}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                    ) : (
                      <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                    )}
                  </button>
                )}
              </div>

              {/* Validation messages */}
              {field.name === 'email' && touchedFields.email && !validateEmail(formData.email) && (
                <p className="text-destructive text-[10px] sm:text-sm mt-1">{t('invalidEmail')}</p>
              )}
              {field.name === 'password' && touchedFields.password && (
                <>
                  {Array.isArray(errors.password) && errors.password.map((error, idx) => (
                    <p key={idx} className="text-destructive text-[10px] sm:text-sm mt-1">{error}</p>
                  ))}
                </>
              )}
            </div>
          ))}

          {/* Forgot Password - only on login */}
          {!isSignUp && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => navigate('/reset-password')}
                className="text-xs sm:text-sm text-primary hover:underline font-medium"
              >
                {t('forgotPassword')}
              </button>
            </div>
          )}

          {isSignUp && (
            <div className="rounded-xl border border-border bg-secondary/20 p-3 sm:p-4">
              <ConsentCheckboxes
                idPrefix="signup-consent"
                part1Checked={consentPart1}
                part2Checked={consentPart2}
                onPart1Change={(checked) => {
                  setConsentPart1(checked);
                  if (checked) setConsentError(false);
                }}
                onPart2Change={setConsentPart2}
                showMarketing
                marketingChecked={marketingOptIn}
                onMarketingChange={setMarketingOptIn}
              />
              {consentError && (
                <p className="text-destructive text-[10px] sm:text-sm mt-2">
                  {t(
                    'consent_part1_required',
                    'Please confirm you are 18+ and agree to the Terms and Privacy Policy to continue.'
                  )}
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading || !isFormValid}
            className="
              w-full 
              bg-primary hover:bg-primary/90 
              text-primary-foreground 
              py-2.5 sm:py-3 
              rounded-xl 
              font-semibold
              text-sm sm:text-base
              min-h-[44px] sm:min-h-[48px]
            "
          >
            {loading ? (
              <AnimatedTextLoader text={t('loading')} />
            ) : (
              isSignUp ? t('continue') : t('login')
            )}
          </Button>
        </motion.form>

        {/* Switch between login/signup */}
        <div className="text-center mt-4 sm:mt-6 text-xs sm:text-sm text-muted-foreground">
          {isSignUp ? (
            <>
              {t('alreadyHaveAccount')}{' '}
              <button
                onClick={() => navigate('/login', { state: location.state })}
                className="text-primary font-semibold hover:underline"
              >
                {t('login')}
              </button>
            </>
          ) : (
            <>
              {t('dontHaveAccount')}{' '}
              <button
                onClick={() => navigate('/sign-up', { state: location.state })}
                className="text-primary font-semibold hover:underline"
              >
                {t('signUp')}
              </button>
            </>
          )}
        </div>

        {isSignUp && (
          <p className="max-w-md mx-auto w-full text-center text-[10px] sm:text-xs text-muted-foreground/80 leading-relaxed mt-6 sm:mt-8">
            {t(
              'google_consent_notice',
              'By continuing with Google, you confirm you are 18+ and agree to our'
            )}{' '}
            <Link
              to="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {t('terms_of_service', 'Terms of Service')}
            </Link>{' '}
            {t('and', 'and')}{' '}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
            >
              {t('privacy_policy', 'Privacy Policy')}
            </Link>
            .
          </p>
        )}
      </div>

      {/* Popup Messages */}
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`
              fixed top-24 left-4 right-4 mx-auto max-w-md
              p-4 rounded-xl shadow-lg
              text-center font-medium
              z-50
              ${popupType === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
              }
            `}
          >
            {popupMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Login;
